<template>
  <div
    class="sidebar sidebar-theme sidebar-main sidebar-fixed sidebar-full"
    :class="{ 'sidebar-main-trial': authStore.showAnyBar }"
    ref="root"
  >
    <div class="sidebar-content" :class="{ 'sidebar-content-trial': authStore.showAnyBar }">
      <div class="card card-sidebar-mobile">
        <div class="sidebar-user sidebar-company" v-if="!authStore.company && !authStore.otherCompanies.length">
          <div class="cursor-pointer">
            <router-link :to="{ name: 'company.create' }" class="text-black">
              <div class="card-body d-flex justify-content-between">
                <div>
                  <div class="sidebar-company--title" >{{ $t('company.actions.create') }}</div>
                </div>
                <ChevronIcon class="company-expand-btn sidebar-company--expand_btn"/>
              </div>
            </router-link>
          </div>
        </div>

        <s-popper
          :placement="getSidebarPlacement()"
          @opened="activatedCompanyMenu"
          @closed="closedCompanyMenu"
          popup-class="sidebar-company--dropdown--open"
          arrow
        >
          <div class="sidebar-user sidebar-company" v-if="authStore.company || authStore.otherCompanies.length">
            <div class="cursor-pointer">
              <div class="card-body d-flex justify-content-between">
                <template v-if="authStore.company">
                  <div>
                    <div class="sidebar-company--title" v-if="authStore.company.title">
                      {{ authStore.company.title }}
                    </div>
                    <div class="sidebar-company--title">{{ companyDisplayName(authStore.company, false) }}</div>
                    <div class="font-size-xs opacity-50">
                      <i class="icon-user-tie mr-1 opacity-50" v-if="!authStore.company.owner"/>
                      {{ $user.role }}
                    </div>
                  </div>
                </template>
                <template v-if="!authStore.company">
                  <div class="font-weight-midbold">{{ $t('extra.choose_company') }}</div>
                </template>
                <ChevronIcon
                  class="company-expand-btn sidebar-company--expand_btn"
                  :class="{ 'sidebar-company--expand_btn--expanded': expandedCompanyMenu }"
                />
              </div>
            </div>
          </div>

          <template #popup>
            <div class="sidebar-company--dropdown">
              <div class="d-flex flex-column" v-if="authStore.company">
                <div class="sidebar-submenu--tab">
                  <div class="sidebar-company--item d-gap-10 bg-transparent">
                    <company-icon/>
                    <span class="font-weight-midbold">{{ $t('extra.administration') }}</span>
                  </div>
                </div>

                <div class="sidebar-submenu--tab">
                  <router-link
                    :to="{ name: 'company.edit', params: { tab: 'info' } }"
                    class="sidebar-company--item"
                  >
                    <i/> {{ $t('company.extra.settings') }}
                  </router-link>
                </div>

                <div class="sidebar-submenu--tab">
                  <router-link
                    v-restrict-trial
                    :to="{ name: 'module.index' }"
                    class="sidebar-company--item"
                  >
                    <i/> <span>{{ $t('module.title') }}</span>
                  </router-link>
                </div>

                <div class="sidebar-submenu--tab" v-if="$auth.can('role.index') && $user.permissions.subscription.employees">
                  <router-link :to="{ name: 'role.index' }" class="sidebar-company--item">
                    <i/> {{ $t('role.index.title') }}
                  </router-link>
                </div>

                <div class="sidebar-submenu--tab" v-if="$auth.can('employee.index')">
                  <router-link v-restrict-trial :to="{ name: 'employee.index' }" class="sidebar-company--item">
                    <i/> {{ $t('employee.index.title') }}
                  </router-link>
                </div>

                <div class="sidebar-submenu--tab" v-if="$auth.can('template.index') && $auth.canSub('email_templates')">
                  <router-link v-restrict-trial :to="{ name: 'template.index' }" class="sidebar-company--item">
                    <i/> {{ $t('template.index.title') }}
                  </router-link>
                </div>

                <div class="sidebar-submenu--tab">
                  <router-link
                    v-restrict-trial
                    :to="{ name: 'events.edit', params: { tab: $auth.can('invoice-email.index') ? 'emails' : 'imports' } }"
                    class="sidebar-company--item"
                  >
                    <i/> {{ $t('events.index.title') }}
                  </router-link>
                </div>
              </div>

              <hr class="my-1" v-if="authStore.company">

              <div class="sidebar-submenu--tab">
                <div class="sidebar-company--item justify-content-between">
                  <div class="d-flex align-items-center d-gap-10">
                    <squares-icon/>
                    <span class="font-weight-midbold">{{ $t('company.extra.other_activities') }}</span>
                  </div>
                  <div>
                    <template v-if="authStore.companyCount < userCompanyLimit">
                      <router-link :to="{ name: 'company.create' }">
                        <create-icon class="sidebar-submenu--create_icon text-primary"/>
                      </router-link>
                    </template>
                  </div>
                </div>
              </div>

              <div class="sidebar-submenu--tab">
                <a @click.prevent="switchCompany(company)" class="sidebar-company--item" href="#" v-for="company in ownedCompanies">
                  <i/>
                  <span>{{ companyDisplayName(company) }}</span>
                </a>
              </div>

              <template v-if="employeeCompanies.length">
                <hr class="my-1">

                <div class="sidebar-submenu--tab">
                  <div class="sidebar-company--item justify-content-between">
                    <div class="d-flex align-items-center d-gap-10">
                      <show-icon/>
                      <span class="font-weight-midbold">{{ $t('company.extra.employee_activities') }}</span>
                    </div>
                  </div>
                </div>
                <div class="sidebar-submenu--tab">
                  <a @click.prevent="switchCompany(company)" class="sidebar-company--item" href="#" v-for="company in employeeCompanies">
                    <i/>
                    {{ companyDisplayName(company) }}
                  </a>
                </div>
              </template>
            </div>
          </template>
        </s-popper>
        <div class="py-tiny-2 px-mini-2" v-if="authStore.invoiceQuota?.limit">
          <div class="v2-title--small">{{ formatCurrency(authStore.invoiceQuota.turnover) }}</div>
          <div class="v2-text--tiny mb-tiny-2 text-v2-tertiary mt-micro">{{ $t('subscription.monthly_turnover') }}</div>
          <usage-bar
            style="height: 4px"
            fill-background="linear-gradient(307.9deg, #08C269 21.89%, #0B71FE 78.12%)"
            background="#D9D9D9"
            class="mb-tiny-2"
            :max="Number(authStore.invoiceQuota.limit)"
            :usage="Number(authStore.invoiceQuota.turnover)"
          />
          <div class="v2-text--tiny text-v2-secondary">{{ $t('subscription.turnover_left', { amount: formatCurrency(authStore.invoiceQuota.left) }) }}</div>
        </div>
        <div class="pb-2">
          <div class="sidebar--action_btn" v-if="showPlanMessage">
            <button @click="openPaymentComparisonModal" class="btn btn-primary w-100">
              {{ $t('subscription.actions.change_plan') }}
            </button>
          </div>

          <div class="sidebar--action_btn sidebar--action_btn--green" v-if="showExtendSubscription">
            <button @click="openPaymentModal" class="btn w-100">
              <database-icon/> {{ $t('subscription.extend') }}
            </button>
          </div>

          <div
            class="sidebar--action_btn"
            v-if="canSeeCreateInvoice"
            v-tippy="{ onShow: () => !$auth.canSub('invoices'), content: $t('subscription.free_plan_limited') }"
          >
            <router-link
              :to="{ name: 'invoice.create' }"
              class="btn btn-primary w-100"
              v-restrict="!$auth.canSub('invoices')"
            >
              <create-icon/> {{ $t('invoice.actions.create') }}
            </router-link>
          </div>
        </div>

        <div class="card-body px-0 pt-0 pb-5" v-if="authStore.company">
          <div class="sidebar-submenu--title">
            <div class="d-flex align-items-center d-gap-10">
              <span>{{ $t('extra.accounting') }}</span>
              <chevron-icon
                @click="hideMenu('accounting')"
                class="sidebar-submenu--expand_btn"
                :class="{ 'sidebar-submenu--expand_btn--expanded': !hideAccountingList }"
              />
            </div>
          </div>
          <ul class="nav nav-sidebar" v-if="!hideAccountingList">
            <li class="nav-item nav-item-submenu" v-if="$auth.can('dashboard')">
              <router-link v-restrict-trial :to="{ name: 'dashboard' }" exact>
                <div class="sidebar-submenu--tab">
                  <div class="sidebar-submenu--main sidebar-submenu--main-hover">
                    <div class="sidebar-submenu--icon">
                      <graph-icon/>
                    </div>
                    <span class="sidebar-submenu--main-title">{{ $t('dashboard.statistics.stats') }}</span>
                  </div>
                </div>
              </router-link>
            </li>
            <li class="nav-item nav-item-submenu">
              <router-link
                :to="{ name: 'invoice.index' }"
                v-if="$auth.can('invoice.index')"
                custom
                v-slot="{ href, navigate }"
              >
                <a v-restrict-trial :href="href" @click="navigate" :class="shouldSkipExactActiveInvoice() ? '' : 'router-link-exact-active router-link-active'">
                  <div class="sidebar-submenu--tab">
                    <div class="sidebar-submenu--main sidebar-submenu--main-hover justify-content-between">
                      <div class="d-flex">
                        <div class="sidebar-submenu--icon">
                          <invoice-icon/>
                        </div>
                        <span class="sidebar-submenu--main-title">{{ $t('sidebar.invoices') }}</span>
                      </div>
                      <div
                        v-tippy="{ onShow: () => !$auth.canSub('invoices'), content: $t('subscription.free_plan_limited') }"
                      >
                        <router-link
                          :to="{ name: 'invoice.create' }"
                          v-if="$auth.can('invoice.create')"
                          v-restrict="!$auth.canSub('invoices')"
                        >
                          <create-icon class="text-primary sidebar-submenu--create_icon"/>
                        </router-link>
                      </div>
                    </div>
                  </div>
                </a>
              </router-link>

              <ul class="nav d-block">
                <li class="nav-item" v-if="$auth.can('invoice.index')">
                  <router-link
                    :to="recentInvoicesRoute"
                    custom
                    v-slot="{ navigate }"
                  >
                    <div class="sidebar-submenu--tab">
                      <a
                        v-restrict-trial
                        @click="navigate"
                        role="link"
                        class="nav-link cursor-pointer sidebar-submenu--nav_link sidebar-submenu--main-hover"
                        :class="{ 'router-link-exact-active router-link-active': isRecentInvoicesActive }"
                      >
                        <span class="sidebar-submenu--sub-title">{{ $t('invoice.index.latest_title') }}</span>
                      </a>
                    </div>
                  </router-link>
                </li>
                <li class="nav-item" v-if="$user?.activeCompany?.country === 'LT' && $auth.can('receipt.index')">
                  <router-link v-restrict-trial :to="{ name: 'receipt.index' }">
                    <div class="sidebar-submenu--tab">
                      <div class="nav-link sidebar-submenu--nav_link sidebar-submenu--main-hover">
                        <span class="sidebar-submenu--sub-title">{{ $t('receipt.index.title') }}</span>
                      </div>
                    </div>
                  </router-link>
                </li>
                <li class="nav-item" v-if="$auth.can('invoice.index')">
                  <router-link
                    :to="draftInvoicesRoute"
                    custom
                    v-slot="{ navigate }"
                  >
                    <div class="sidebar-submenu--tab">
                      <a
                        v-restrict-trial
                        @click="navigate"
                        role="link"
                        class="nav-link cursor-pointer sidebar-submenu--nav_link sidebar-submenu--main-hover"
                        :class="{ 'router-link-exact-active router-link-active': isDraftInvoicesActive }"
                      >
                        <span class="sidebar-submenu--sub-title">{{ $t('invoice.index.draft_title') }}</span>
                      </a>
                    </div>
                  </router-link>
                </li>
              </ul>
            </li>
            <li class="nav-item nav-item-submenu" v-if="$auth.can('client.index')">
              <router-link v-restrict-trial :to="{ name: 'client.index' }">
                <div class="sidebar-submenu--tab">
                  <div class="sidebar-submenu--main sidebar-submenu--main-hover">
                    <div class="sidebar-submenu--icon">
                      <clients-icon/>
                    </div>
                    <span class="sidebar-submenu--main-title">{{ $t('client.index.title') }}</span>
                  </div>
                </div>
              </router-link>
            </li>
            <li class="nav-item nav-item-submenu" v-if="$auth.can('product.index')">
              <router-link v-restrict-trial :to="{ name: 'product.index' }">
                <div class="sidebar-submenu--tab">
                  <div class="sidebar-submenu--main sidebar-submenu--main-hover">
                    <div class="sidebar-submenu--icon">
                      <box-icon/>
                    </div>
                    <span class="sidebar-submenu--main-title">{{ $t('product.index.title') }}</span>
                  </div>
                </div>
              </router-link>
              <ul class="nav d-block">
                <li class="nav-item" v-if="$auth.can('product.categories')">
                  <router-link v-restrict-trial :to="{ name: 'product-category.index' }">
                    <div class="sidebar-submenu--tab">
                      <div class="nav-link sidebar-submenu--nav_link sidebar-submenu--main-hover">
                        <span class="sidebar-submenu--sub-title">{{ $t('warehouse_category.index.title') }}</span>
                      </div>
                    </div>
                  </router-link>
                </li>
              </ul>
            </li>
            <li class="nav-item nav-item-submenu" v-if="$auth.can('project.index')">
              <router-link v-restrict-trial :to="{ name: 'project.index' }">
                <div class="sidebar-submenu--tab">
                  <div class="sidebar-submenu--main sidebar-submenu--main-hover">
                    <div class="sidebar-submenu--icon">
                      <projects-icon/>
                    </div>
                    <span class="sidebar-submenu--main-title">{{ $t('project.index.title') }}</span>
                  </div>
                </div>
              </router-link>
            </li>
          </ul>
          <div>
            <div class="sidebar-submenu--title">
              <div class="d-flex align-items-center d-gap-10">
                <span>{{ $t('module.title') }}</span>
                <ChevronIcon
                  @click="hideMenu('modules')"
                  class="sidebar-submenu--expand_btn"
                  :class="{ 'sidebar-submenu--expand_btn--expanded': !hideModulesList }"
                />
              </div>
            </div>
            <ul class="nav nav-sidebar" v-if="!hideModulesList">
              <li class="nav-item" v-if="showPeriodicInvoices">
                <router-link v-restrict-trial :to="{ name: 'periodic-invoice.index' }">
                  <div class="sidebar-submenu--tab">
                    <div class="sidebar-submenu--main sidebar-submenu--module-hover">
                      <div class="sidebar-submenu--module_icon">
                        <refresh-icon/>
                      </div>
                      <span class="sidebar-submenu--main-title">{{ $t('periodic_invoice.index.title') }}</span>
                    </div>
                  </div>
                </router-link>
              </li>
              <li class="nav-item nav-item-submenu" v-if="$module.can('work_act') && $auth.can('work_act.index')">
                <router-link :to="{ name: 'work-act.index' }" v-restrict-trial>
                  <div class="sidebar-submenu--tab">
                    <div class="sidebar-submenu--main sidebar-submenu--module-hover">
                      <div class="d-flex align-items-center">
                        <div class="sidebar-submenu--module_icon">
                          <file-icon/>
                        </div>
                        <span class="sidebar-submenu--main-title">{{ $t('work-act.index.acts') }}</span>
                      </div>
                    </div>
                  </div>
                </router-link>
              </li>
              <li class="nav-item" v-if="$module.can('sip') && authStore.company.show_bank_statements && $auth.can('sip.index')">
                <router-link v-restrict-trial :to="{ name: 'sip-statements.payments.index' }">
                  <div class="sidebar-submenu--tab">
                    <div class="sidebar-submenu--main sidebar-submenu--module-hover">
                      <div class="sidebar-submenu--module_icon">
                        <bank-pencil-icon/>
                      </div>
                      <span class="sidebar-submenu--main-title">{{ $t('sip.index.payments_statement') }}</span>
                    </div>
                  </div>
                </router-link>
              </li>
              <li class="nav-item nav-item-submenu" v-if="$module.can('commercial-suggestion') && $auth.can('commercial-suggestion.index')">
                <router-link :to="{ name: 'commercial-suggestion.index' }" v-restrict-trial>
                  <div class="sidebar-submenu--tab">
                    <div class="sidebar-submenu--main sidebar-submenu--module-hover">
                      <div class="sidebar-submenu--module_icon">
                        <piggy-bank-icon/>
                      </div>
                      <span class="sidebar-submenu--main-title">{{ $t('commercial-suggestion.index.title') }}</span>
                    </div>
                  </div>
                </router-link>
              </li>
              <li class="nav-item nav-item-submenu" v-if="!$feature.can('expense-new') && $module.can('expenses') && $auth.can('expense.index')">
                <router-link v-restrict-trial :to="{ name: 'expense.index' }">
                  <div class="sidebar-submenu--tab">
                    <div class="sidebar-submenu--main sidebar-submenu--module-hover">
                      <div class="sidebar-submenu--module_icon">
                        <ticket-icon/>
                      </div>
                      <span class="sidebar-submenu--main-title">{{ $t('expense.index.title') }}</span>
                    </div>
                  </div>
                </router-link>

                <ul class="nav d-block">
                  <li class="nav-item" v-if="$feature.can('periodic-expense')">
                    <router-link
                      v-restrict-trial
                      :to="{ name: 'periodic-expense.index' }"
                    >
                      <div class="sidebar-submenu--tab">
                        <div class="nav-link sidebar-submenu--nav_link sidebar-submenu--module-hover">
                          <span class="sidebar-submenu--sub-title">{{ $t('periodic-expenses.index.title') }}</span>
                        </div>
                      </div>
                    </router-link>
                  </li>
                  <li class="nav-item">
                    <router-link v-restrict-trial :to="{ name: 'fixed-asset.index' }">
                      <div class="sidebar-submenu--tab">
                        <div class="nav-link sidebar-submenu--nav_link sidebar-submenu--module-hover">
                          <span class="sidebar-submenu--sub-title">{{ $t('fixed_asset.index.title') }}</span>
                        </div>
                      </div>
                    </router-link>
                  </li>
                  <li class="nav-item" v-if="$auth.can('expense.index')">
                    <router-link
                      v-restrict-trial
                      :to="{ name: 'expense-documents.index' }"
                    >
                      <div class="sidebar-submenu--tab">
                        <div class="nav-link sidebar-submenu--nav_link sidebar-submenu--module-hover">
                          <span class="sidebar-submenu--sub-title">{{ $t('expense-document.index.title') }}</span>
                        </div>
                      </div>
                    </router-link>
                  </li>
                  <li class="nav-item" v-if="$auth.can('expense.index')">
                    <router-link
                      v-restrict-trial
                      :to="{ name: 'expense-document-import.create' }"
                    >
                      <div class="sidebar-submenu--tab">
                        <div class="nav-link sidebar-submenu--nav_link sidebar-submenu--module-hover">
                          <span class="sidebar-submenu--sub-title">{{ $t('expense-document-import.index.title') }}</span>
                        </div>
                      </div>
                    </router-link>
                  </li>
                </ul>
              </li>
              <li class="nav-item" v-if="$feature.can('expense-new') && $module.can('expenses') && $auth.can('expense.index')">
                <router-link v-restrict-trial :to="{ name: 'expense-new.index' }">
                  <div class="sidebar-submenu--tab">
                    <div class="sidebar-submenu--main sidebar-submenu--module-hover">
                      <div class="sidebar-submenu--module_icon">
                        <ticket-icon/>
                      </div>
                      <span class="sidebar-submenu--main-title">{{ $t('expense-new.index.title') }}</span>
                    </div>
                  </div>
                </router-link>
                <ul class="nav d-block">
                  <li class="nav-item">
                    <router-link v-restrict-trial :to="{ name: 'fixed-asset.index' }">
                      <div class="sidebar-submenu--tab">
                        <div class="nav-link sidebar-submenu--nav_link sidebar-submenu--module-hover">
                          <span class="sidebar-submenu--sub-title">{{ $t('fixed_asset.index.title') }}</span>
                        </div>
                      </div>
                    </router-link>
                  </li>
                  <li class="nav-item">
                    <router-link v-restrict-trial :to="{ name: 'expense-documents.index' }">
                      <div class="sidebar-submenu--tab">
                        <div class="nav-link sidebar-submenu--nav_link sidebar-submenu--module-hover">
                          <span class="sidebar-submenu--sub-title">{{ $t('expense-new.documents_title') }}</span>
                        </div>
                      </div>
                    </router-link>
                  </li>
                  <li class="nav-item" v-if="$auth.can('expense.create')">
                    <router-link v-restrict-trial :to="{ name: 'expense-new.import' }">
                      <div class="sidebar-submenu--tab">
                        <div class="nav-link sidebar-submenu--nav_link sidebar-submenu--module-hover">
                          <span class="sidebar-submenu--sub-title">{{ $t('expense-new.import_title') }}</span>
                        </div>
                      </div>
                    </router-link>
                  </li>
                </ul>
              </li>
              <li class="nav-item" v-if="showWarehouse">
                <router-link v-restrict-trial :to="{ name: 'warehouse.index' }">
                  <div class="sidebar-submenu--tab">
                    <div class="sidebar-submenu--main sidebar-submenu--module-hover">
                      <div class="sidebar-submenu--module_icon">
                        <warehouse-icon/>
                      </div>
                      <span class="sidebar-submenu--main-title">{{ $t('warehouse.index.title') }}</span>
                    </div>
                  </div>
                </router-link>
                <ul class="nav d-block">
                  <li class="nav-item" v-if="!$feature.can('expense-new') && $auth.can('warehouse-purchase.index')">
                    <router-link v-restrict-trial :to="{ name: 'warehouse-purchase.index' }">
                      <div class="sidebar-submenu--tab">
                        <div class="nav-link sidebar-submenu--nav_link sidebar-submenu--module-hover">
                          <span class="sidebar-submenu--sub-title">{{ $t('warehouse_purchase.index.title') }}</span>
                        </div>
                      </div>
                    </router-link>
                  </li>
                  <li class="nav-item" v-if="!$feature.can('expense-new') && $auth.can('purchase-documents-import.index')">
                    <router-link
                      v-restrict-trial
                      :to="{ name: 'purchase-document-import.create' }"
                    >
                      <div class="sidebar-submenu--tab">
                        <div class="nav-link sidebar-submenu--nav_link sidebar-submenu--module-hover">
                          <span class="sidebar-submenu--sub-title">{{ $t('purchase-document-import.index.title') }}</span>
                        </div>
                      </div>
                    </router-link>
                  </li>
                </ul>
              </li>
              <li class="nav-item" v-if="showWaybills">
                <router-link v-restrict-trial :to="{ name: 'waybill.index' }">
                  <div class="sidebar-submenu--tab">
                    <div class="sidebar-submenu--main sidebar-submenu--module-hover">
                      <div class="sidebar-submenu--module_icon">
                        <truck-icon/>
                      </div>
                      <span class="sidebar-submenu--main-title">{{ $t('waybill.index.title') }}</span>
                    </div>
                  </div>
                </router-link>

                <ul class="nav d-block">
                  <li class="nav-item">
                    <router-link v-restrict-trial :to="{ name: 'carrier.index' }">
                      <div class="sidebar-submenu--tab">
                        <div class="nav-link sidebar-submenu--nav_link sidebar-submenu--module-hover">
                          <span class="sidebar-submenu--sub-title">{{ $t('carrier.index.title') }}</span>
                        </div>
                      </div>
                    </router-link>
                  </li>
                </ul>
              </li>
              <li class="nav-item" v-if="$module.can('iv') && $auth.can('journal.index') && authStore.company && authStore.company.show_journal">
                <router-link v-restrict-trial :to="{ name: 'journal.index' }">
                  <div class="sidebar-submenu--tab">
                    <div class="sidebar-submenu--main sidebar-submenu--module-hover">
                      <div class="sidebar-submenu--module_icon">
                        <journal-icon/>
                      </div>
                      <span class="sidebar-submenu--main-title">{{ $t('journal.index.title') }}</span>
                    </div>
                  </div>
                </router-link>
              </li>
              <li class="nav-item" v-if="$module.can('reports') && $auth.can('reports.index')">
                <router-link v-restrict-trial :to="{ name: 'reports.index' }">
                  <div class="sidebar-submenu--tab">
                    <div class="sidebar-submenu--main sidebar-submenu--module-hover">
                      <div class="sidebar-submenu--module_icon">
                        <file-chart-pie-icon/>
                      </div>
                      <span class="sidebar-submenu--main-title">{{ $t('reports.index.title') }}</span>
                    </div>
                  </div>
                </router-link>
              </li>
              <li class="nav-item" v-if="showCompanyContracts">
                <router-link v-restrict-trial :to="{ name: 'company-contracts.index' }">
                  <div class="sidebar-submenu--tab">
                    <div class="sidebar-submenu--main sidebar-submenu--module-hover">
                      <div class="sidebar-submenu--module_icon">
                        <file-text/>
                      </div>
                      <span class="sidebar-submenu--main-title">{{ $t('company-contract.index.title') }}</span>
                    </div>
                  </div>
                </router-link>
                <ul class="nav d-block">
                  <li class="nav-item">
                    <router-link v-restrict-trial :to="{ name: 'company-contract-payments.index' }">
                      <div class="sidebar-submenu--tab">
                        <div class="nav-link sidebar-submenu--nav_link sidebar-submenu--module-hover">
                          <span class="sidebar-submenu--sub-title">{{ $t('company-contract-payment.index.title') }}</span>
                        </div>
                      </div>
                    </router-link>
                  </li>
                </ul>
              </li>
              <li class="nav-item">
                <router-link v-restrict-trial :to="{ name: 'module.index' }">
                  <div class="sidebar-submenu--all-modules-container">
                    <div class="sidebar-submenu--tab sidebar-submenu--all-modules-tab">
                      <div class="d-flex align-items-center justify-content-between">
                        <span class="sidebar-submenu--all-modules-title">{{ $t('module.links.all_modules') }}</span>
                        <redirect-icon/>
                      </div>
                    </div>
                  </div>
                </router-link>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div v-if="showOnboardingGuideBtn" class="onboarding-guide__sidebar-btn text-white ledger-font" @click="authStore.setOnboardingGuideVisible(true)">
        <progress-ring-icon class="onboarding-guide__sidebar-btn-progress-ring"/>
        <div>
          <div class="d-flex d-gap-8 align-items-center v2-text--small-2 onboarding-guide__sidebar-btn-text">
            <span>{{ $t('onboarding-guide.easy_to_start') }}</span>
            <chevron-icon style="margin-top: 1px;" class="icon--tiny rotate-90"/>
          </div>
          <div class="v2-text--mini">{{ $t('onboarding-guide.finish_steps') }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import ClientsIcon from '@/components/icons/clients.vue';
  import CompanyIcon from '@/components/icons/company.vue';
  import CreateIcon from '@/components/icons/create.vue';
  import InvoiceIcon from '@/components/icons/invoice.vue';
  import ProjectsIcon from '@/components/icons/projects.vue';
  import SquaresIcon from '@/components/icons/squares.vue';
  import { routerService } from '@/core';
  import { landingRoute } from '@/landing';
  import {
    invoiceTypeFilterTypesMap,
    subscriptionTypesMap,
  } from '@/data/type-maps';
  import gsap from 'gsap';
  import { addEventListener, removeEventListener, requestAnimationFrame } from '@/utils/browser';
  import { companyDisplayName } from '@/utils/formatting';
  import { format as formatCurrency } from '@/utils/currency';
  import GraphIcon from '@/components/icons/graph.vue';
  import { restrictTrial as vRestrictTrial, restrict as vRestrict } from '@/directives';
  import { useSidebarStore } from '@/stores/sidebar';
  import { useSubscriptionStore } from '@/stores/subscription';
  import { useAuthStore } from '@/stores/auth.ts';
  import BoxIcon from '@/components/icons/box.vue';
  import { computed, onMounted, watch, ref, onBeforeUnmount } from 'vue';
  import { authService } from '@/models/auth';
  import { moduleService } from '@/models/module-service.ts';
  import { featureFlagService } from '@/models/feature-flag';
  import { filtersFromQueryParams, filtersToQueryParams } from '@/utils/filters/query';
  import { useRoute } from 'vue-router';
  import ChevronIcon from '@/components/icons/chevron-icon.vue';
  import DatabaseIcon from '@/components/icons/database.vue';
  import RedirectIcon from '@/components/icons/redirect.vue';
  import ShowIcon from '@/components/icons/show.vue';
  import ProgressRingIcon from '@/components/icons-v2/progress-ring-icon.vue';
  import UsageBar from '@/components/usage-bar/usage-bar.vue';
  import PiggyBankIcon from '@/components/icons-v2/piggy-bank-icon.vue';
  import TicketIcon from '@/components/icons-v2/ticket-icon.vue';
  import WarehouseIcon from '@/components/icons-v2/warehouse-icon.vue';
  import TruckIcon from '@/components/icons-v2/truck-icon.vue';
  import FileChartPieIcon from '@/components/icons-v2/file-chart-pie-icon.vue';
  import FileText from '@/components/icons-v2/file-text.vue';
  import JournalIcon from '@/components/icons-v2/journal-icon.vue';
  import FileIcon from '@/components/icons-v2/file-icon.vue';
  import RefreshIcon from '@/components/icons-v2/refresh-icon.vue';
  import BankPencilIcon from '@/components/icons-v2/bank-pencil-icon.vue';

  const ANIMATION_DURATION = 0.2;
  const SOLID_OPACITY = 0.65;
  const GUTTER = 15;
  const DIRECTION_THRESHOLD = 10;

  const touchStartX = ref(0);
  const touchStartY = ref(0);
  const distanceX = ref(0);
  const leftPosition = ref(0);
  const direction = ref(null);
  const raf = ref(false);
  const width = ref(0);
  const endListenerBound = ref(false);
  const moveListenerBound = ref(false);

  const STATE = {
    CLOSED: 1,
    OPENING: 2,
    OPEN: 3,
    CLOSING: 4,
    PANNING: 5,
  };

  const emit = defineEmits(['fullClose']);

  const backdropEl = ref(null);
  const state = ref(STATE.OPEN);
  const expandedCompanyMenu = ref(false);
  const sidebarStore = useSidebarStore();
  const authStore = useAuthStore();
  const subscriptionStore = useSubscriptionStore();
  const companySwitchLoading = ref(false);
  const route = useRoute();
  const root = ref(null);
  const hideModulesList = ref(false);
  const hideAccountingList = ref(false);

  const draftInvoicesRoute = computed(() => ({
    name: 'invoice.index',
    query: featureFlagService.can('new-invoice-filter-modal')
      ? filtersToQueryParams({ filters: [{ field: 'type', operator: 'eq', value: invoiceTypeFilterTypesMap.draft }] })
      : { type: invoiceTypeFilterTypesMap.draft },
  }));

  const recentInvoicesRoute = { name: 'invoice.index', query: { show_latest: true } };

  const isDraftInvoicesActive = computed(() => route.path === '/invoices'
    && (route.query.type === invoiceTypeFilterTypesMap.draft
      || filtersFromQueryParams(route.query).filters.some(
        (clause) => clause.field === 'type' && clause.value === invoiceTypeFilterTypesMap.draft,
      )));

  const isRecentInvoicesActive = computed(
    () => route.path === '/invoices' && route.query.show_latest === 'true',
  );

  const ownedCompanies = computed(() => authStore.otherCompanies.filter(it => it.owner));

  const employeeCompanies = computed(() => authStore.otherCompanies.filter(it => !it.owner));

  const mobileOpen = computed(() => sidebarStore.mobileOpen);

  const showPlanMessage = computed(() =>
    authStore.user?.permissions?.subscription.tag === 'free' && authStore.company?.owner);

  const showExtendSubscription = computed(() => authStore.expirationDays !== null && authStore.expirationDays < 10 && authStore.company?.owner && !authStore.user.permissions.user_subscription.subscription_recurring);

  const userCompanyLimit = computed(() => authStore.user?.permissions?.user_subscription.company_limit);

  const showWaybills = computed(() => Boolean(authService.can('waybill.index') && (moduleService.can('waybill'))));

  const showWarehouse = computed(() => Boolean(moduleService.can('warehouse')) && Boolean(authService.can('warehouse.index')));

  const showPeriodicInvoices = computed(() => Boolean(authService.can('invoice.index') && authService.canSub('recurring_invoice') && moduleService.can('periodic-invoice')));

  const showCompanyContracts = computed(() => Boolean(authService.can('company-contracts.index') && (moduleService.can('company-contract'))));

  const canSeeCreateInvoice = computed(() =>
    authStore.user?.permissions.user_subscription.tag !== 'free' &&
    authStore.company &&
    !authStore.company.archived_at &&
    authService.canSub('invoices') &&
    !authStore.isAdmin &&
    authService.can('invoice.create'));

  const showOnboardingGuideBtn = computed(() => {
    return authStore.user?.onboarding?.show_onboarding_guide && authStore.user?.permissions?.subscription.tag !== subscriptionTypesMap.free;
  });

  watch(() => mobileOpen.value, (newValue) => {
    requestAnimationFrame(() => {
      if (newValue) {
        _openSidebar();
      } else {
        _closeSidebar();
      }
    });
  });

  onMounted(() => {
    backdropEl.value = document.getElementById('og-sidebar-backdrop');
    addEventListener(backdropEl.value, 'click', (event) => {
      event.stopImmediatePropagation();
      sidebarStore.updateMobileOpen(false);
      _closeSidebar();
    });

    addEventListener(document, 'touchstart', touchStart, false);

    width.value = root.value.offsetWidth;
  });

  try {
    hideModulesList.value = localStorage.getItem('hideModulesList') === 'true' ? hideModulesList.value = true : hideModulesList.value = false;
    hideAccountingList.value = localStorage.getItem('hideAccountingList') === 'true' ? hideAccountingList.value = true : hideAccountingList.value = false;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (e) { /* empty */ }

  if (window.innerWidth >= 768) {
    state.value = STATE.OPEN;
  } else {
    state.value = STATE.CLOSED;
  }

  onBeforeUnmount(() => {
    removeEventListener(document, 'touchstart', touchStart);
  });

  function openPaymentModal() {
    subscriptionStore.setModalVisible(true);
  }

  function openPaymentComparisonModal() {
    subscriptionStore.setShowComparison(true);
  }

  async function switchCompany(company) {
    if (companySwitchLoading.value) {
      return;
    }

    companySwitchLoading.value = true;

    await authStore.changeActiveCompany({
      id: company.id,
    });

    companySwitchLoading.value = false;

    if (!authStore.company?.owner) {
      routerService.push(landingRoute());
    }

    expandedCompanyMenu.value = false;
  }

  function touchStart(event) {
    touchStartX.value = event.touches[0].clientX;
    touchStartY.value = event.touches[0].clientY;
    distanceX.value = 0;
    direction.value = null;

    if (state.value !== STATE.OPEN) {
      if (touchStartX.value > GUTTER) {
        return;
      }

      event.preventDefault();
    }

    requestAnimationFrame(() => {
      backdropEl.value.style.display = 'block';
      root.value?.classList.add('og-sidebar--has-touch');

      if (!moveListenerBound.value) {
        addEventListener(document, 'touchmove', touchMove, false);
        moveListenerBound.value = true;
      }

      raf.value = true;
    });
  }

  function touchMove(event) {
    if (!raf.value) {
      return;
    }

    requestAnimationFrame(() => {
      distanceX.value = event.touches[0].clientX - touchStartX.value;
      const distanceY = event.touches[0].clientY - touchStartY.value;

      // determine move direction
      if (direction.value === null) {
        if (Math.abs(distanceX.value) > DIRECTION_THRESHOLD) {
          direction.value = 'x';
          if (root.value) {
            leftPosition.value = parseInt(window.getComputedStyle(root.value).getPropertyValue('left'));
          }
        } else if (Math.abs(distanceY) > DIRECTION_THRESHOLD) {
          direction.value = 'y';
        }
      }

      if (direction.value === 'x') {
        if (!endListenerBound.value) {
          addEventListener(document, 'touchend', touchEnd);
          endListenerBound.value = true;
        }

        let moveDistance = Math.min(leftPosition.value - (distanceX.value * -1), 0);
        if (moveDistance < -width.value) {
          moveDistance = -width.value;
        }

        const distancePercentage = (width.value - Math.abs(moveDistance)) * 100 / width.value;
        const opacity = SOLID_OPACITY * (distancePercentage / 100);

        if (distancePercentage === 100) {
          state.value = STATE.OPEN;
        } else if (distancePercentage === 0) {
          state.value = STATE.CLOSED;
        } else {
          state.value = STATE.PANNING;
        }

        if (root.value) {
          root.value.style.left = `${moveDistance}px`;
          root.value.style.touchAction = 'pan-x';
        }
        backdropEl.value.style.opacity = opacity;
      } else if (direction.value === null) {
        if (event.cancelable) {
          event.preventDefault();
        }
      }

      raf.value = true;
    });
  }

  function touchEnd() {
    requestAnimationFrame(() => {
      if (root.value) {
        root.value.style.touchAction = '';

        const position = parseInt(window.getComputedStyle(root.value).getPropertyValue('left'));
        if (position > -(width.value * 0.75)) {
          _openSidebar(position);
        } else {
          _closeSidebar(position);
        }

        root.value.classList.remove('og-sidebar--has-touch');
        _cleanupListeners();
        raf.value = false;
      }
    });
  }

  function _openSidebar() {
    if (state.value === STATE.OPEN || state.value === STATE.OPENING) {
      return;
    }

    state.value = STATE.OPENING;

    if (window.innerWidth <= 767) {
      document.body.style.overflow = 'hidden';

      gsap.to(root.value, {
        duration: ANIMATION_DURATION,
        left: 0,
        onComplete: () => {
          state.value = STATE.OPEN;
          if (root.value) {
            root.value.classList.remove('og-sidebar--has-touch');
          }
        },
      });

      backdropEl.value.style.display = 'block';
      gsap.to(backdropEl.value, { opacity: SOLID_OPACITY, duration: ANIMATION_DURATION });
    } else {
      backdropEl.value.style.display = 'none';
      document.body.style.overflow = 'unset';
    }
  }

  function _closeSidebar() {
    if (window.innerWidth <= 767) {
      document.body.style.overflow = 'unset';
    }

    if (state.value === STATE.CLOSED || state.value === STATE.CLOSING) {
      if (state.value === STATE.CLOSED) {
        // If the user fully closed the sidebar manually, fix backdrop.
        backdropEl.value.style.display = 'none';
      }

      return;
    }

    emit('fullClose');

    state.value = STATE.CLOSING;
    raf.value = false;

    gsap.to(root.value, {
      duration: ANIMATION_DURATION,
      left: -width.value,
      onComplete: () => {
        state.value = STATE.CLOSED;

        if (root.value) {
          root.value.classList.remove('og-sidebar--has-touch');
        }
        backdropEl.value.style.display = 'none';

        _cleanupListeners();
      },
    });

    gsap.to(backdropEl.value, { opacity: 0, duration: ANIMATION_DURATION });
  }

  function _cleanupListeners() {
    if (moveListenerBound.value) {
      removeEventListener(document, 'touchmove', touchMove);
      moveListenerBound.value = false;
    }

    if (endListenerBound.value) {
      removeEventListener(document, 'touchend', touchEnd);
      endListenerBound.value = false;
    }
  }

  function hideMenu(type) {
    switch (type) {
      case 'modules':
        hideModulesList.value = !hideModulesList.value;
        localStorage.setItem('hideModulesList', String(hideModulesList.value));
        break;
      case 'accounting':
        hideAccountingList.value = !hideAccountingList.value;
        localStorage.setItem('hideAccountingList', String(hideAccountingList.value));
        break;
    }
  }

  function activatedCompanyMenu() {
    expandedCompanyMenu.value = true;
  }

  function closedCompanyMenu() {
    expandedCompanyMenu.value = false;
  }

  function getSidebarPlacement() {
    return window.innerWidth <= 767 ? 'bottom' : 'right-start';
  }

  // the parent item keeps its highlight off while one of its sub-views owns it
  function shouldSkipExactActiveInvoice() {
    return !route.path.startsWith('/invoices')
      || isDraftInvoicesActive.value
      || isRecentInvoicesActive.value;
  }
</script>
